import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FloatingLabel, Form, Image } from "react-bootstrap";
import { toast } from "react-toastify";

import Utils from "../configs/Utils";
import Enums from "../configs/Enums";
import Loading from "../Components/Common/Loading";

import "../assets/css/account.scss";

const ResetPassword = () => {
  const passwordRef = useRef(null);
  const { token } = useParams();
  const [formLoading, setFormLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordErrMsg, setPasswordErrMsg] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordErrMsg, setConfirmPasswordErrMsg] = useState("");
  const [resetDone, setResetDone] = useState(false);

  useEffect(() => {
    Utils.setPageTitle("Reset password");
    passwordRef.current?.focus();
  }, []);

  const clearErrMessage = (field) => {
    if (field === "password") {
      setPasswordErrMsg("");
      return;
    }

    setConfirmPasswordErrMsg("");
  };

  const validateForm = () => {
    let valid = true;

    if (!password.length) {
      setPasswordErrMsg("This field is required!");
      valid = false;
    } else if (!Enums.Regex.PASS.test(password)) {
      setPasswordErrMsg("Please enter a stronger password!");
      valid = false;
    }

    if (!confirmPassword.length) {
      setConfirmPasswordErrMsg("This field is required!");
      valid = false;
    } else if (confirmPassword !== password) {
      setConfirmPasswordErrMsg("Passwords do not match!");
      valid = false;
    }

    if (!token) {
      toast.error("Reset token is missing or invalid.");
      valid = false;
    }

    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setFormLoading(true);

    try {
      const data = await Utils._apiRequest({
        path: Enums.endpoints.auth.RESET_PASSWORD,
        pathParams: { token },
        type: "POST",
        payload: { password },
      });

      if (data?.errors?.password) {
        setPasswordErrMsg(data.errors.password);
        return;
      }

      if (data?.errors?.message) {
        toast.error(data.errors.message);
        return;
      }

      if (data?.message) {
        toast.success(data.message);
      } else {
        toast.success("Password reset successfully. You can now log in.");
      }

      setResetDone(true);
    } catch (error) {
      toast.error(error.message || "Failed to reset password.");
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <main className="page-main account-page container">
      <section className="reset-password-wrapper">
        <Form
          className={`p-4 p-md-5 border overflow-hidden shadow rounded-3 bg-body-tertiary${formLoading ? " loading" : ""}`}
          onSubmit={handleSubmit}
        >
          <Loading show={formLoading} />
          <h1 className="logo text-center">
            <Image
              src={Utils.getPublicImage("/assets/images/logo-main.png")}
              width={145}
              alt=""
            />
          </h1>

          <h2 className="h4 mb-2">Create a new password</h2>
          <p className="text-muted mb-4">
            Your new password must include uppercase, lowercase, number, and one of !@#$%.
          </p>

          {resetDone ? (
            <div className="alert alert-success" role="status">
              Password updated successfully. You can sign in with your new password now.
            </div>
          ) : null}

          <FloatingLabel
            controlId="resetPasswordInput"
            label="New password"
            className="mb-3"
          >
            <Form.Control
              ref={passwordRef}
              type="password"
              value={password}
              className={passwordErrMsg !== "" ? " is-invalid" : ""}
              placeholder="New password"
              onChange={(e) => {
                setPassword(e.target.value);
                clearErrMessage("password");
              }}
            />

            {passwordErrMsg !== "" ? (
              <p className="invalid-feedback">{passwordErrMsg}</p>
            ) : null}
          </FloatingLabel>

          <FloatingLabel
            controlId="resetConfirmPasswordInput"
            label="Confirm new password"
            className="mb-3"
          >
            <Form.Control
              type="password"
              value={confirmPassword}
              className={confirmPasswordErrMsg !== "" ? " is-invalid" : ""}
              placeholder="Confirm new password"
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                clearErrMessage("confirmPassword");
              }}
            />

            {confirmPasswordErrMsg !== "" ? (
              <p className="invalid-feedback">{confirmPasswordErrMsg}</p>
            ) : null}
          </FloatingLabel>

          <button className="w-100 btn btn-lg btn-primary" type="submit">
            Update password
          </button>

          <div className="footer">
            <p className="mb-1">
              Back to <Link to="/login">login</Link>
            </p>
            <p className="mb-0">
              Need another link? <Link to="/forgot-password">Request reset email</Link>
            </p>
          </div>
        </Form>
      </section>
    </main>
  );
};

export default ResetPassword;